import React, { useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group'

import { makeGetCurrentUser } from '../selectors/users';
import { makeGetCurrentTeam } from '../selectors/teams';
import { makeGetIsOnTrial, makeGetSubscriptionIsExpired, makeGetRemainingTrial, makeGetSubscriptionIsPastDue } from '../selectors/billing';

import { getToday } from '../utils/date';
import { setUserTrackingProp } from '../services/events-tracker-service';

import { AlertContext } from 'context/AlertContext';
import { WebexContext } from 'context/WebexContext';

import Alert from './alert';

import BrowserNotificationRequest from './browser-notification-request';
import SubscriptionAlert from './subscription-alert';
import SocketErrorAlert from './socket-error-alert';
import UploadingFilesAlert from './uploading-files-alert';
import BackgroundTaskProgress from './background-task-progress';
import NetworkErrorAlert from 'components/NetworkErrorAlert';
import QuotaAlerts from 'components/QuotaAlerts';

const AppHead = ({ history, trialEnd, isExpired, isPastDue, subscriptionStatus, isOnTrial, remainingTrial, createProjectVisible, socketError, subscriptionAlertVisible }) => {
    const alertCtx = useContext(AlertContext);
    const webexCtx = useContext(WebexContext);

    const onboarding = useMemo(() => {
        return history.location.pathname.indexOf('signup') >= 2 || createProjectVisible;
    }, [history.location.pathname, createProjectVisible]);

    useEffect(() => {
        setUserTrackingProp('on_trial', isOnTrial);
    }, [isOnTrial]);

    useEffect(() => {
        setUserTrackingProp('is_expired', isExpired);
    }, [isExpired]);

    useEffect(() => {
        setUserTrackingProp('is_past_due', isPastDue);
    }, [isPastDue]);

    useEffect(() => {
        setUserTrackingProp('subscription_status', subscriptionStatus);
    }, [subscriptionStatus]);

    useEffect(() => {
        setUserTrackingProp('trial_at', trialEnd);
    }, [trialEnd]);

    useEffect(() => {
        setUserTrackingProp('trial_expires', remainingTrial);
    }, [remainingTrial]);

    return (
        <div className="app-head">
            <div className="app-hard-alerts">
                <TransitionGroup className="alerts-holder" appear={true}>
                    {
                        subscriptionAlertVisible ? (
                            <CSSTransition key="trial-alert" timeout={300} classNames="alert">
                                <SubscriptionAlert history={history} />
                            </CSSTransition>
                        ) : null
                    }

                    <NetworkErrorAlert />

                    <SocketErrorAlert hasError={socketError} />

                    {
                        !subscriptionAlertVisible && !onboarding && !webexCtx?.inApp ? (
                            <CSSTransition key="browser-notification-request" timeout={300} classNames="alert">
                                <BrowserNotificationRequest />
                            </CSSTransition>
                        ) : null
                    }
                </TransitionGroup>
            </div>

            <div className="app-alerts">
                <TransitionGroup className="alerts-holder" appear={true}>
                    <UploadingFilesAlert />

                    <BackgroundTaskProgress />

                    <QuotaAlerts alert={alertCtx?.quotaAlert} />

                    {
                        alertCtx?.alerts.map(alert => (
                            <CSSTransition key={alert.id} timeout={300} classNames="alert">
                                <Alert type={alert.type}>{ alert.content }</Alert>
                            </CSSTransition>
                        ))
                    }
                </TransitionGroup>
            </div>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getCurrentTeam = makeGetCurrentTeam();
    const getIsOnTrial = makeGetIsOnTrial();
    const getSubscriptionIsExpired = makeGetSubscriptionIsExpired();
    const getRemainingTrial = makeGetRemainingTrial();
    const getSubscriptionIsPastDue = makeGetSubscriptionIsPastDue();

    return ({ users, teams, files, appProps }) => {
        const currentUser = getCurrentUser(users);
        const currentTeam = getCurrentTeam(teams);
        const isOnTrial = getIsOnTrial(currentTeam);
        const remainingTrial = isOnTrial && getRemainingTrial(currentTeam, getToday().valueOf());
        const isExpired = getSubscriptionIsExpired(currentTeam);
        const isPastDue = getSubscriptionIsPastDue(currentTeam);

        return {
            isExpired,
            isPastDue,
            isOnTrial,
            remainingTrial,
            currentTeam,
            trialEnd: currentTeam?.trial_end,
            subscriptionStatus: currentTeam?.subscription_status,
            subscriptionAlertVisible: isExpired || isPastDue || (isOnTrial && remainingTrial <= 0),
            createProjectVisible: appProps.createProjectVisible,
            socketError: appProps.socketError
        }
    };
};

export default connect(makeMapStateToProps)(AppHead);
